/* eslint-disable import-helpers/order-imports */
import { Box, Flex, Grid, Link, SimpleGrid, Skeleton, Text, VStack } from '@chakra-ui/react';

import type { CustomLinksGroup } from 'types/footerLinks';
import FooterLinkItem from './FooterLinkItem';
import type { GridProps } from '@chakra-ui/react';
import IntTxsIndexingStatus from './IntTxsIndexingStatus';
import NetworkAddToWallet from 'ui/shared/NetworkAddToWallet';
import React from 'react';
import type { ResourceError } from 'lib/api/resources';
import config from 'configs/app';
import getApiVersionUrl from './utils/getApiVersionUrl';
import useApiQuery from 'lib/api/useApiQuery';
import useFetch from 'lib/hooks/useFetch';
import useIssueUrl from 'lib/hooks/useIssueUrl';
import { useQuery } from '@tanstack/react-query';

const MAX_LINKS_COLUMNS = 4;

const Footer = () => {

  const { data: backendVersionData } = useApiQuery('config_backend_version', {
    queryOptions: {
      staleTime: Infinity,
    },
  });
  const apiVersionUrl = getApiVersionUrl(backendVersionData?.backend_version);
  const issueUrl = useIssueUrl(backendVersionData?.backend_version);
  const BLOCKSCOUT_LINKS = [
    {
      icon: 'edit' as const,
      iconSize: '16px',
      text: 'Submit an issue',
      url: issueUrl,
    },
    {
      icon: 'social/canny' as const,
      iconSize: '20px',
      text: 'Feature request',
      url: 'https://blockscout.canny.io/feature-requests',
    },
    {
      icon: 'social/git' as const,
      iconSize: '18px',
      text: 'Contribute',
      url: 'https://github.com/blockscout/blockscout',
    },
    {
      icon: 'social/tweet' as const,
      iconSize: '18px',
      text: 'Twitter',
      url: 'https://www.twitter.com/blockscoutcom',
    },
    {
      icon: 'social/discord' as const,
      iconSize: '24px',
      text: 'Discord',
      url: 'https://discord.gg/blockscout',
    },
    {
      icon: 'discussions' as const,
      iconSize: '20px',
      text: 'Discussions',
      url: 'https://github.com/orgs/blockscout/discussions',
    },
    {
      icon: 'donate' as const,
      iconSize: '20px',
      text: 'Donate',
      url: 'https://github.com/sponsors/blockscout',
    },
  ];

  const frontendLink = (() => {
    if (config.UI.footer.frontendVersion) {
      return config.UI.footer.frontendVersion;
    }

    if (config.UI.footer.frontendCommit) {
      return config.UI.footer.frontendCommit;
    }

    return null;
  })();

  const fetch = useFetch();

  const { isPlaceholderData, data: linksData } = useQuery<unknown, ResourceError<unknown>, Array<CustomLinksGroup>>({
    queryKey: [ 'footer-links' ],
    queryFn: async() => fetch(config.UI.footer.links || '', undefined, { resource: 'footer-links' }),
    enabled: Boolean(config.UI.footer.links),
    staleTime: Infinity,
    placeholderData: [],
  });

  const colNum = isPlaceholderData ? 1 : Math.min(linksData?.length || Infinity, MAX_LINKS_COLUMNS) + 1;

  const renderNetworkInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    return (
      <Flex
        gridArea={ gridArea }
        flexWrap="wrap"
        columnGap={ 8 }
        rowGap={ 6 }
        mb={{ base: 5, lg: 10 }}
        _empty={{ display: 'none' }}
      >
        { !config.UI.indexingAlert.intTxs.isHidden && <IntTxsIndexingStatus/> }
        <NetworkAddToWallet/>
      </Flex>
    );
  }, []);

  const renderProjectInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    return (
      <Box gridArea={ gridArea }>
        { renderNetworkInfo({ lg: 'network' }) }

        <Link fontSize="xs" href="https://www.kubscan.com/">kubscan.com</Link>
        <Text mt={ 3 } fontSize="xs">
            KUB Scan is a tool for inspecting and analyzing blockchain for KUB network.
        </Text>
        <VStack spacing={ 1 } mt={ 6 } alignItems="start">
          { apiVersionUrl && (
            <Text fontSize="xs">
              Backend: { backendVersionData?.backend_version }
            </Text>
          ) }
          { frontendLink && (
            <Text fontSize="xs">
              Frontend: { frontendLink }
            </Text>
          ) }
        </VStack>
      </Box>
    );
  }, [ apiVersionUrl, backendVersionData?.backend_version, frontendLink ]);

  const containerProps: GridProps = {
    as: 'footer',
    px: { base: 4, lg: 12 },
    py: { base: 4, lg: 9 },
    borderTop: '1px solid',
    borderColor: 'divider',
    gridTemplateColumns: { base: '1fr', lg: 'minmax(auto, 470px) 1fr' },
    columnGap: { lg: '32px', xl: '100px' },
  };

  if (config.UI.footer.links) {
    return (
      <Grid { ...containerProps }>
        <div>
          { renderNetworkInfo() }
          { renderProjectInfo() }
        </div>

        <Grid
          gap={{ base: 6, lg: colNum === MAX_LINKS_COLUMNS + 1 ? 2 : 8, xl: 12 }}
          gridTemplateColumns={{
            base: 'repeat(auto-fill, 160px)',
            lg: `repeat(${ colNum }, 135px)`,
            xl: `repeat(${ colNum }, 160px)`,
          }}
          justifyContent={{ lg: 'flex-end' }}
          mt={{ base: 8, lg: 0 }}
        >
          {
            ([
              { title: 'Blockscout', links: BLOCKSCOUT_LINKS },
              ...(linksData || []),
            ])
              .slice(0, colNum)
              .map(linkGroup => (
                <Box key={ linkGroup.title }>
                  <Skeleton fontWeight={ 500 } mb={ 3 } display="inline-block" isLoaded={ !isPlaceholderData }>{ linkGroup.title }</Skeleton>
                  <VStack spacing={ 1 } alignItems="start">
                    { linkGroup.links.map(link => <FooterLinkItem { ...link } key={ link.text } isLoading={ isPlaceholderData }/>) }
                  </VStack>
                </Box>
              ))
          }
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      { ...containerProps }
      gridTemplateAreas={{
        lg: `
          "network links-top"
        `,
      }}
    >
      { renderProjectInfo({ lg: 'network' }) }

      <SimpleGrid
        marginTop={{ base: '48px', lg: '0' }}
        maxWidth="1000px"
        justifySelf={{ base: 'start', lg: 'end' }}
        // columns={{ base: 2, sm: 3, md: 3 }}
        columns={{ base: 2, sm: 2, md: 2 }}
        spacing="80px"
      >
        <Box>
          <Box marginBottom="12px">
            KUB
          </Box>
          { BITKUB_CHAIN_LINK.map(link => <FooterLinkItem { ...link } key={ link.text }/>) }
        </Box>
        <Box>
          <Box marginBottom="12px">
          Developers
          </Box>
          { DEVELOPER_LINK.map(link => <FooterLinkItem { ...link } key={ link.text }/>) }
        </Box>
        { /* <Box>
          <Box marginBottom="12px">
          Community
          </Box>
          { COMMUNITY_LINK.map(link => <FooterLinkItem { ...link } key={ link.text }/>) }
        </Box> */ }
      </SimpleGrid>
    </Grid>
  );
};

export default React.memo(Footer);

const BITKUB_CHAIN_LINK = [
  {
    text: 'Home',
    url: 'https://www.kubscan.com/',
  },
  {
    text: 'Ecosystem',
    url: 'https://www.kubchain.com/ecosystem',
  },
  {
    text: 'Bridge',
    url: 'https://bridge.kubchain.com/',
  },
  {
    text: 'Faucet',
    url: 'https://faucet.kubchain.com/',
  },
  {
    text: 'Port',
    url: 'https://www.bkcport.com/',
  },
  {
    text: 'Oracle',
    url: 'https://www.bkcoracle.com/',
  },
];

const DEVELOPER_LINK = [
  {
    text: 'Documentation',
    url: 'https://docs.kubchain.org/',
  },
  {
    text: 'Developer Center',
    url: 'https://developers.kubchain.com/',
  },
  {
    text: 'Console',
    url: 'https://console.kubchain.com/',
  },
  {
    text: 'GitHub',
    url: 'https://github.com/kub-chain',
  },
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const COMMUNITY_LINK = [
  {
    text: 'Discord',
    url: 'https://discord.com/invite/U86SzznKTm',
  },
  {
    text: 'X',
    url: 'https://twitter.com/bitkubchain',
  },
  {
    text: 'Telegram',
    url: 'https://t.me/BitkubChainOfficial',
  },
  {
    text: 'Facebook',
    url: 'https://www.facebook.com/bitkubchainofficial',
  },
  {
    text: 'Medium',
    url: 'https://medium.com/bitkubchain',
  },
];
